import React, { use, useEffect, useRef, useState } from "react";
import { useTemplateStore } from "@/zustand/templateStore";
import { useProductData } from "@/zustand/productStore";
import { useCartData } from "@/zustand/cartStore.js";
import s from "@/components/Buttons/ExpressCheckoutButtons.module.scss";

const GooglePayButton = ({ height, onGooglePaySubmit, requirePolicy }) => {
  const btnWrapRef = useRef(null);
  const [paymentsClient, setPaymentsClient] = useState(null);
  const [isAvailable, setIsAvailable] = useState(false);
  const setIsGooglePayAvailable = useTemplateStore((s) => s.setIsGooglePayAvailable);
  const handlePaymentMethodChange = useTemplateStore((s) => s.handlePaymentMethodChange);
  const { paymentsConfig } = useProductData();
  const { gpayGateway, gpayGatewayMerchant, gpayMerchantId, dynamicDescriptor, googlePayConfigured } =
    paymentsConfig || {};
  const bypassRef = useRef(false);

  useEffect(() => {
    setIsGooglePayAvailable(Boolean(isAvailable && googlePayConfigured));
  }, [isAvailable, googlePayConfigured]);

  useEffect(() => {
    if (window.google && window.google.payments) return;
    const s = document.createElement("script");
    s.src = "https://pay.google.com/gp/p/js/pay.js";
    s.async = true;
    s.onload = init;
    document.head.appendChild(s);
    return () => s.remove();
  }, []);

  useEffect(() => {
    if (window.google && window.google.payments) init();
  }, []);

  useEffect(() => {
    if (isAvailable && btnWrapRef.current && window.googlePayConfig) {
      const { client, baseCardPaymentMethod } = window.googlePayConfig;

      const button = client.createButton({
        onClick: () => {
          onGooglePayClick(client, baseCardPaymentMethod);
        },
        buttonColor: "black",
        buttonType: "plain",
        buttonRadius: 6,
        buttonSizeMode: "fill",
        allowedPaymentMethods: [baseCardPaymentMethod],
      });
      button.style.display = "block";
      button.style.width = "100%";
      button.style.marginBottom = "16px";
      button.style.minWidth = "100%";
      button.style.maxWidth = "100%";

      btnWrapRef.current.innerHTML = "";
      btnWrapRef.current.appendChild(button);

      setTimeout(() => {
        const gpayContainer = btnWrapRef.current?.querySelector(".gpay-card-info-container");
        if (gpayContainer) {
          gpayContainer.style.width = "100%";
          gpayContainer.style.minWidth = "100%";
          gpayContainer.style.outline = "none";
        }
      }, 100);

      // Clean version guard
      const gpayBtn = btnWrapRef.current.querySelector("button");
      const guard = async (e) => {
        if (bypassRef.current) return;

        handlePaymentMethodChange("googlepay");

        if (typeof requirePolicy !== "function") return;

        e.preventDefault();
        e.stopImmediatePropagation();
        const ok = await requirePolicy();
        if (!ok) return;

        bypassRef.current = true;
        e.currentTarget.click();
        bypassRef.current = false;
      };
      gpayBtn?.addEventListener("click", guard, true);
      return () => gpayBtn?.removeEventListener("click", guard, true);
    }
  }, [isAvailable, requirePolicy]);

  const init = () => {
    if (!window.google || !window.google.payments) {
      console.error("GooglePayButton: Google Pay API not loaded");
      return;
    }

    const client = new window.google.payments.api.PaymentsClient({
      environment: "PRODUCTION", // PRODUCTION || TEST
    });
    setPaymentsClient(client);

    const baseCardPaymentMethod = {
      type: "CARD",
      parameters: {
        allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"],
        allowedCardNetworks: ["VISA", "MASTERCARD", "AMEX", "DISCOVER"],
        billingAddressRequired: false,
      },
      tokenizationSpecification: {
        type: "PAYMENT_GATEWAY",
        parameters: {
          gateway: gpayGateway, // "adyen, stripe"
          gatewayMerchantId: gpayGatewayMerchant, // "Rara..."
        },
      },
    };

    client
      .isReadyToPay({
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: [baseCardPaymentMethod],
      })
      .then((res) => {
        setIsAvailable(res.result);
        if (res.result) {
          window.googlePayConfig = { client, baseCardPaymentMethod };
        } else {
        }
      })
      .catch((err) => {
        setIsAvailable(false);
      });
  };

  const onGooglePayClick = async (client, baseCardPaymentMethod) => {
    const { currency_code, country_code, allowed_payment_methods } = useProductData.getState();
    const { cartTotal } = useCartData.getState();

    const paymentDataRequest = {
      apiVersion: 2,
      apiVersionMinor: 0,
      allowedPaymentMethods: [baseCardPaymentMethod],
      merchantInfo: {
        merchantName: dynamicDescriptor, // "Checkoutera"
        merchantId: gpayMerchantId, // "BCR2DN7XXXXXXXXX"
      },
      transactionInfo: {
        totalPriceStatus: "FINAL",
        totalPrice: cartTotal || "0.00",
        currencyCode: currency_code || "USD",
        countryCode: country_code || "US",
      },
      emailRequired: true,
      shippingAddressRequired: true,
      shippingAddressParameters: {
        phoneNumberRequired: true,
      },
    };

    try {
      const paymentData = await client.loadPaymentData(paymentDataRequest);

      const token = paymentData?.paymentMethodData?.tokenizationData?.token;
      const email = paymentData?.email;
      const addr = paymentData?.shippingAddress || {};
      const phone = addr?.phoneNumber || "";
      const name = addr?.name || "";

      const [firstName = "", ...restName] = name.trim().split(/\s+/);
      const lastName = restName.join(" ");

      const googlePayData = {
        google_pay_token: token,
        customer: {
          full_name: name,
          first_name: firstName,
          last_name: lastName,
          phone,
          email,
          address: addr?.address1 || addr?.addressLine1 || "",
          address2: addr?.address2 || addr?.addressLine2 || "",
          city: addr?.locality || "",
          state: addr?.administrativeArea || "",
          zip: addr?.postalCode || "",
          country: addr?.countryCode || "",
        },
      };

      if (onGooglePaySubmit) {
        const controller = new AbortController();
        const timeout = setTimeout(() => {
          // Max 30 seconds to process payment
          controller.abort();
        }, 30000);

        try {
          await onGooglePaySubmit(googlePayData, { signal: controller.signal });
          clearTimeout(timeout);
        } catch (err) {
          clearTimeout(timeout);
          throw err;
        }
      }
    } catch (err) {
      console.error("GooglePayButton: Error during payment processing:", err);
      if (err.statusCode !== "CANCELED") {
        alert("Google Pay processing failed");
      } else {
        console.log("GooglePayButton: Payment was cancelled by user");
      }
    }
  };

  if (!isAvailable) {
    return null;
  }

  return (
    <div
      ref={btnWrapRef}
      style={{
        height: height || 56,
        width: "100%",
      }}
      className={s["google-pay-container"]}
    />
  );
};

export default GooglePayButton;
